export type MicState = "idle" | "requesting" | "capturing" | "stopped" | "error";

export interface LiveAudioDebugState {
  micState: MicState;
  speechActive: boolean;
  forwardingEnabled: boolean;
  holdingUntilSpeechStart: boolean;
  peak: number;
  rms: number;
  forwardedChunkCount: number;
  lastForwardedAt: number | null;
}

interface LiveAudioInputOptions {
  audioContext?: AudioContext;
  mixDestination?: AudioNode;
  onDebug?: (state: LiveAudioDebugState) => void;
  onChunk: (chunk: ArrayBuffer, mimeType: string) => void;
  onSpeechEnd?: () => void;
  onSpeechStart?: () => void;
  onStateChange?: (state: MicState) => void;
}

const SPEECH_LEVEL_THRESHOLD = 0.02;
const SPEECH_RMS_THRESHOLD = 0.01;
export const SPEECH_START_DEBOUNCE_MS = 120;
export const SPEECH_END_DEBOUNCE_MS = 1200;

function measureInputLevel(input: Float32Array) {
  let peak = 0;
  let sumSquares = 0;

  for (let index = 0; index < input.length; index += 1) {
    const value = input[index] ?? 0;
    const absoluteValue = Math.abs(value);
    if (absoluteValue > peak) {
      peak = absoluteValue;
    }
    sumSquares += value * value;
  }

  return {
    peak,
    rms: Math.sqrt(sumSquares / Math.max(input.length, 1)),
  };
}

function downsampleToPcm16(
  input: Float32Array,
  inputSampleRate: number,
  targetSampleRate: number
): ArrayBuffer {
  if (targetSampleRate >= inputSampleRate) {
    const output = new Int16Array(input.length);
    for (let index = 0; index < input.length; index += 1) {
      const value = Math.max(-1, Math.min(1, input[index] ?? 0));
      output[index] = value < 0 ? value * 32768 : value * 32767;
    }
    return output.buffer;
  }

  const ratio = inputSampleRate / targetSampleRate;
  const outputLength = Math.max(1, Math.round(input.length / ratio));
  const output = new Int16Array(outputLength);
  let offset = 0;

  for (let index = 0; index < outputLength; index += 1) {
    const nextOffset = Math.min(input.length, Math.round((index + 1) * ratio));
    let accumulator = 0;
    let count = 0;

    while (offset < nextOffset) {
      accumulator += input[offset] ?? 0;
      count += 1;
      offset += 1;
    }

    const sample = count > 0 ? accumulator / count : 0;
    const normalized = Math.max(-1, Math.min(1, sample));
    output[index] = normalized < 0 ? normalized * 32768 : normalized * 32767;
  }

  return output.buffer;
}

export class LiveAudioInput {
  private readonly audioContext: AudioContext;
  private readonly mixDestination: AudioNode | undefined;
  private readonly onDebug: ((state: LiveAudioDebugState) => void) | undefined;
  private readonly onSpeechEnd: (() => void) | undefined;
  private readonly onSpeechStart: (() => void) | undefined;
  private readonly ownContext: boolean;
  private readonly onChunk: (chunk: ArrayBuffer, mimeType: string) => void;
  private readonly onStateChange: ((state: MicState) => void) | undefined;
  private chunkHoldUntilSpeechStart = false;
  private forwardingEnabled = true;
  private gainNode: GainNode | null = null;
  private processorNode: ScriptProcessorNode | null = null;
  private speechActive = false;
  private speechEndTimer: number | null = null;
  private speechStartTimer: number | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private stream: MediaStream | null = null;
  private state: MicState = "idle";
  private forwardedChunkCount = 0;
  private lastForwardedAt: number | null = null;
  private lastLevel = { peak: 0, rms: 0 };

  constructor(options: LiveAudioInputOptions) {
    this.audioContext = options.audioContext ?? new AudioContext();
    this.mixDestination = options.mixDestination;
    this.ownContext = !options.audioContext;
    this.onDebug = options.onDebug;
    this.onChunk = options.onChunk;
    this.onSpeechEnd = options.onSpeechEnd;
    this.onSpeechStart = options.onSpeechStart;
    this.onStateChange = options.onStateChange;
    this.emitDebugState();
  }

  async start() {
    if (this.state === "capturing" || this.state === "requesting") {
      return;
    }

    this.setState("requesting");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      this.stream = stream;
      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume();
      }

      this.sourceNode = this.audioContext.createMediaStreamSource(stream);
      this.processorNode = this.audioContext.createScriptProcessor(4096, 1, 1);
      this.gainNode = this.audioContext.createGain();
      this.gainNode.gain.value = 0;

      this.processorNode.onaudioprocess = (event) => {
        this.processInput(event.inputBuffer.getChannelData(0));
      };

      this.sourceNode.connect(this.processorNode);
      if (this.mixDestination) {
        this.sourceNode.connect(this.mixDestination);
      }
      this.processorNode.connect(this.gainNode);
      this.gainNode.connect(this.audioContext.destination);
      this.setState("capturing");
    } catch {
      await this.stop();
      this.setState("error");
    }
  }

  async stop() {
    this.chunkHoldUntilSpeechStart = false;
    this.clearSpeechStartTimer();
    this.clearSpeechEndTimer();
    this.finishSpeechActivity();
    this.processorNode?.disconnect();
    this.sourceNode?.disconnect();
    this.gainNode?.disconnect();

    this.processorNode = null;
    this.sourceNode = null;
    this.gainNode = null;

    if (this.stream) {
      for (const track of this.stream.getTracks()) {
        track.stop();
      }
      this.stream = null;
    }

    if (this.ownContext && this.audioContext.state !== "closed") {
      await this.audioContext.close();
    }

    if (this.state !== "error") {
      this.setState("stopped");
    }
    this.emitDebugState();
  }

  pause() {
    this.chunkHoldUntilSpeechStart = false;
    this.forwardingEnabled = false;
    this.clearSpeechStartTimer();
    this.clearSpeechEndTimer();
    this.finishSpeechActivity();
    this.emitDebugState();
  }

  resume() {
    this.chunkHoldUntilSpeechStart = false;
    this.forwardingEnabled = true;
    this.emitDebugState();
  }

  holdChunksUntilSpeechStart() {
    if (!this.forwardingEnabled) {
      return;
    }

    this.chunkHoldUntilSpeechStart = true;
    this.emitDebugState();
  }

  private clearSpeechEndTimer() {
    if (this.speechEndTimer !== null) {
      window.clearTimeout(this.speechEndTimer);
      this.speechEndTimer = null;
    }
  }

  private clearSpeechStartTimer() {
    if (this.speechStartTimer !== null) {
      window.clearTimeout(this.speechStartTimer);
      this.speechStartTimer = null;
    }
  }

  private finishSpeechActivity() {
    if (!this.speechActive) {
      return;
    }

    this.speechActive = false;
    this.onSpeechEnd?.();
    this.emitDebugState();
  }

  private processInput(input: Float32Array) {
    this.lastLevel = measureInputLevel(input);
    this.emitDebugState();
    this.updateSpeechActivity(this.lastLevel);
    if (!this.shouldForwardChunks()) {
      return;
    }

    const pcm = downsampleToPcm16(input, this.audioContext.sampleRate, 16000);
    this.forwardedChunkCount += 1;
    this.lastForwardedAt = Date.now();
    this.emitDebugState();
    this.onChunk(pcm, "audio/pcm;rate=16000");
  }

  private shouldForwardChunks() {
    return this.forwardingEnabled && !this.chunkHoldUntilSpeechStart;
  }

  private updateSpeechActivity(level: { peak: number; rms: number }) {
    if (!this.forwardingEnabled) {
      this.clearSpeechStartTimer();
      this.clearSpeechEndTimer();
      this.finishSpeechActivity();
      this.emitDebugState();
      return;
    }

    const hasSpeech = level.peak >= SPEECH_LEVEL_THRESHOLD || level.rms >= SPEECH_RMS_THRESHOLD;

    if (hasSpeech) {
      this.clearSpeechEndTimer();
      if (!this.speechActive) {
        if (this.speechStartTimer === null) {
          this.speechStartTimer = window.setTimeout(() => {
            this.speechStartTimer = null;
            if (!this.forwardingEnabled || this.speechActive) {
              return;
            }

            this.chunkHoldUntilSpeechStart = false;
            this.speechActive = true;
            this.emitDebugState();
            this.onSpeechStart?.();
          }, SPEECH_START_DEBOUNCE_MS);
        }
      }
      return;
    }

    this.clearSpeechStartTimer();

    if (!this.speechActive || this.speechEndTimer !== null) {
      return;
    }

    this.speechEndTimer = window.setTimeout(() => {
      this.speechEndTimer = null;
      this.finishSpeechActivity();
    }, SPEECH_END_DEBOUNCE_MS);
  }

  private setState(nextState: MicState) {
    if (this.state === nextState) {
      return;
    }

    this.state = nextState;
    this.onStateChange?.(nextState);
    this.emitDebugState();
  }

  private emitDebugState() {
    this.onDebug?.({
      micState: this.state,
      speechActive: this.speechActive,
      forwardingEnabled: this.forwardingEnabled,
      holdingUntilSpeechStart: this.chunkHoldUntilSpeechStart,
      peak: this.lastLevel.peak,
      rms: this.lastLevel.rms,
      forwardedChunkCount: this.forwardedChunkCount,
      lastForwardedAt: this.lastForwardedAt,
    });
  }
}
